import type { EventsCapability, EventsSession } from './api/types';
import { can } from './capabilities';

export type EventsSection = {
  label: string;
  path: string;
  capability: EventsCapability;
  /** `reconciliation` sections are the only ones offered when events are off. */
  availability: 'enabled' | 'reconciliation';
};

export const RECONCILIATION_PATH = '/events/reconciliation';

export const bookingPath = (bookingId: number) =>
  `/events/bookings/${bookingId}`;

export const SETTINGS_TABS = [
  { key: 'venue', label: 'Venue' },
  { key: 'spaces', label: 'Spaces' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'pricing', label: 'Pricing' },
] as const;

export type SettingsTab = (typeof SETTINGS_TABS)[number]['key'];

export const settingsPath = (tab: SettingsTab = 'venue') =>
  `/events/settings/${tab}`;

export const EVENTS_SECTIONS: EventsSection[] = [
  {
    label: 'Overview',
    path: '/events',
    capability: 'ops.read',
    availability: 'enabled',
  },
  {
    label: 'Inquiries',
    path: '/events/inquiries',
    capability: 'crm.read',
    availability: 'enabled',
  },
  {
    // The backend gates GET pipeline/ on crm.read (the design spec's ops.read
    // would show the section to kitchen roles and then fail with 403).
    label: 'Pipeline',
    path: '/events/pipeline',
    capability: 'crm.read',
    availability: 'enabled',
  },
  {
    label: 'Approvals',
    path: '/events/approvals',
    capability: 'approval.read',
    availability: 'enabled',
  },
  {
    label: 'Settings',
    path: '/events/settings',
    capability: 'settings.venue',
    availability: 'enabled',
  },
  {
    label: 'Reconciliation',
    path: RECONCILIATION_PATH,
    capability: 'reconciliation.read',
    availability: 'reconciliation',
  },
];

export function visibleSections(session: EventsSession): EventsSection[] {
  const availability = session.reconciliation_only
    ? 'reconciliation'
    : 'enabled';
  return EVENTS_SECTIONS.filter(
    (section) =>
      section.availability === availability && can(session, section.capability),
  );
}
